<template>
  <div>
    <section :crass="$style.wrapper">
      <header :class="$style.header">
        <h2 :class="$style.title">
          自由入力に対する自動応答 チャットルーム設定
        </h2>
        <ButtonClose @click="emit('close')" />
      </header>
    </section>
    <section :class="$style.body">
      <div>
        <p>
          ※
          本機能を利用するには別途以下URL先で自動応答を利用中にする必要があります。
        </p>
        <p>
          <RouterLink :to="{ name: 'autoChatReplySetting' }">
            設定 > 自動応答チャットメッセージ
          </RouterLink>
        </p>
      </div>
      <div :class="$style.status">
        <div :class="[$style.status__buttons, $style.statusButtons]">
          <label
            v-for="option of statusOptions"
            :key="option.label"
            :class="[$style.option, $style.statusButtons__option]"
          >
            <DolphinRadioButton
              name="normalMessageChatReplyStatusType"
              :value="option.value"
              :modelValue="matchSettingDisabled"
              @update:modelValue="option.handler"
            />
            <span :class="$style.option__text">
              {{ option.label }}
            </span>
          </label>
        </div>
      </div>
      <div :class="$style.submitButtons">
        <FlatButton
          variant="primary"
          @click="handleSubmit"
          :disabled="isSubmitDisabled"
          >適用
        </FlatButton>
        <FlatButton variant="base" @click="emit('close')">閉じる</FlatButton>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { captureException } from '@sentry/vue'
import { useAlert } from '@/libs/alertPortal'
import { useLogger } from '@/logger/logger'
import { type RoomID } from '@/types/app'

import ButtonClose from '@/components/atoms/ButtonClose.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'

import {
  useDisableNormalMessageChatReplyMatchSettingMutation,
  useEnableNormalMessageChatReplyMatchSettingMutation,
} from './NormalMessageChatReplyModal.generated'

const emit = defineEmits<{
  (event: 'submit'): void
  (event: 'close'): void
}>()

const props = defineProps<{
  roomId: RoomID
  normalMessageChatReplyDisabled: boolean
}>()

const matchSettingDisabled = ref(props.normalMessageChatReplyDisabled)

const statusOptions = [
  {
    value: false,
    label: '利用',
    handler: () => {
      matchSettingDisabled.value = false
    },
  },
  {
    value: true,
    label: '停止',
    handler: () => {
      matchSettingDisabled.value = true
    },
  },
]

const alert = useAlert()
const logger = useLogger()

const { mutate: enableMatchSetting } =
  useEnableNormalMessageChatReplyMatchSettingMutation()
const { mutate: disableMatchSetting } =
  useDisableNormalMessageChatReplyMatchSettingMutation()

const isSubmitting = ref(false)
const isSubmitDisabled = computed(
  () =>
    isSubmitting.value ||
    props.normalMessageChatReplyDisabled === matchSettingDisabled.value
)

const handleSubmit = async () => {
  isSubmitting.value = true
  try {
    const input = { roomID: props.roomId.toString() }

    if (matchSettingDisabled.value) {
      logger.ltlPeople.event.disableNormalMessageChatReplyMatchSetting(
        logger.buildMeta(),
        {
          data: {
            roomId: props.roomId,
          },
        }
      )
      await disableMatchSetting({ input })
    } else {
      logger.ltlPeople.event.enableNormalMessageChatReplyMatchSetting(
        logger.buildMeta(),
        {
          data: {
            roomId: props.roomId,
          },
        }
      )
      await enableMatchSetting({ input })
    }
  } catch (error) {
    captureException(error)
    alert.error(
      '自由入力に対する自動応答設定の保存に失敗しました。再度時間をおいてお試しください。'
    )
    isSubmitting.value = false
    return
  }
  emit('submit')
  emit('close')
  alert.success('自由入力に対する自動応答設定を保存しました')
  return
}
</script>

<style lang="scss" module>
.wrapper {
  width: 90vw;
  max-width: 620px;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.title {
  font-size: 16px;
  font-weight: bold;
}

.body {
  padding: 10px 40px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.status {
  padding: 16px;

  &__buttons {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.statusButtons {
  &__option {
    &:nth-child(n + 2) {
      margin-left: 24px;
    }
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.submitButtons {
  :nth-child(n + 2) {
    margin-left: 16px;
  }
}
</style>
